import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Target, ArrowRight, CheckCircle2, Play, Clock, Sparkles, Brain } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const PATHWAY_ICONS = {
  stabilization: '🧘',
  brain_state: '🧠',
  cognitive: '💭',
  execution: '⚡',
  identity: '🎯'
};

export default function UpgradePath() {
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPathways();
  }, []);

  const fetchPathways = async () => {
    try {
      const response = await api.get('/pathways');
      setPathways(response.data.pathways || []);
    } catch (error) {
      console.error('Error fetching pathways:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (pathwayId, status) => {
    try {
      await api.put(`/pathways/${pathwayId}/status?status=${status}`);
      toast.success(`Pathway ${status === 'active' ? 'activated' : 'completed'}!`);
      fetchPathways();
    } catch (error) {
      toast.error('Failed to update pathway');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading pathways...</p>
        </div>
      </div>
    );
  }

  if (pathways.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <Target className="h-16 w-16 text-muted-foreground/50 mb-4" strokeWidth={1} />
        <h2 className="text-xl font-semibold mb-2">No Upgrade Pathways Yet</h2>
        <p className="text-muted-foreground mb-6">Complete an assessment to receive personalized upgrade recommendations.</p>
        <Link to="/assessment/full">
          <Button className="glow-primary" data-testid="start-assessment">
            Start Diagnostic
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  const activePathways = pathways.filter(p => p.status === 'active');
  const recommendedPathways = pathways.filter(p => p.status === 'recommended');
  const completedPathways = pathways.filter(p => p.status === 'completed');

  return (
    <div className="space-y-8" data-testid="upgrade-path-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Upgrade Pathways</h1>
        <p className="text-muted-foreground">Structured improvement protocols based on your diagnostic results</p>
      </div>

      {/* Active Pathways */}
      {activePathways.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Play className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
            <h2 className="text-lg font-semibold">Active Pathways</h2>
            <Badge variant="outline" className="ml-2">{activePathways.length}</Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activePathways.map((pathway, index) => (
              <PathwayCard 
                key={pathway.pathway_id} 
                pathway={pathway}
                index={index}
                onComplete={() => updateStatus(pathway.pathway_id, 'completed')}
              />
            ))}
          </div>
        </section>
      )}

      {/* Recommended Pathways */}
      {recommendedPathways.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <h2 className="text-lg font-semibold">Recommended Pathways</h2>
            <Badge variant="outline" className="ml-2">{recommendedPathways.length}</Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendedPathways.map((pathway, index) => (
              <PathwayCard 
                key={pathway.pathway_id} 
                pathway={pathway}
                index={index}
                onActivate={() => updateStatus(pathway.pathway_id, 'active')}
              />
            ))}
          </div>
        </section>
      )}

      {/* Completed Pathways */}
      {completedPathways.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-lg font-semibold">Completed Pathways</h2>
            <Badge variant="outline" className="ml-2">{completedPathways.length}</Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {completedPathways.map((pathway, index) => (
              <PathwayCard 
                key={pathway.pathway_id} 
                pathway={pathway}
                index={index}
                completed
              />
            ))}
          </div>
        </section>
      )}

      {/* Pathway Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Pathway System Overview</CardTitle>
            <CardDescription>
              Five upgrade pathways designed to optimize different aspects of your mental operating system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {[
                { type: 'stabilization', name: 'Stabilization', focus: 'Sleep, Recovery, Stress' },
                { type: 'brain_state', name: 'Brain State', focus: 'Focus, Calm, Transitions' },
                { type: 'cognitive', name: 'Cognitive', focus: 'Thought Patterns, Clarity' },
                { type: 'execution', name: 'Execution', focus: 'Habits, Action, Consistency' },
                { type: 'identity', name: 'Identity', focus: 'Values, Purpose, Alignment' }
              ].map((p, index) => (
                <div 
                  key={p.type}
                  className="p-4 rounded-lg bg-secondary/30 text-center border border-border/50"
                >
                  <span className="text-2xl mb-2 block">{PATHWAY_ICONS[p.type]}</span>
                  <h4 className="font-medium text-sm">{p.name}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{p.focus}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function PathwayCard({ pathway, index, onActivate, onComplete, completed }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className={`bg-card border-border/50 ${completed ? 'opacity-70' : ''}`}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{PATHWAY_ICONS[pathway.pathway_type]}</span>
              <div>
                <CardTitle className="text-lg">{pathway.title}</CardTitle>
                <Badge 
                  variant={pathway.status === 'active' ? 'default' : 'secondary'}
                  className="mt-1"
                >
                  {pathway.status}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{pathway.description}</p>
          
          <div>
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-primary hover:underline flex items-center gap-1"
              data-testid={`expand-pathway-${pathway.pathway_id}`}
            >
              {expanded ? 'Hide' : 'Show'} Daily Practices
              <ArrowRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </button>
            
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-3 space-y-2"
              >
                {pathway.daily_practices?.map((practice, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-muted-foreground">{practice}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </div>

          {!completed && (
            <div className="flex gap-2 pt-2">
              {pathway.status === 'recommended' && onActivate && (
                <Button 
                  onClick={onActivate}
                  className="flex-1"
                  data-testid={`activate-pathway-${pathway.pathway_id}`}
                >
                  <Play className="mr-2 h-4 w-4" />
                  Start Pathway
                </Button>
              )}
              {pathway.status === 'active' && onComplete && (
                <Button 
                  onClick={onComplete}
                  variant="outline"
                  className="flex-1"
                  data-testid={`complete-pathway-${pathway.pathway_id}`}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Mark Complete
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
